import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { adminApi, errMsg, imgUrl } from "@/lib/api";
import { PageTitle, Btn, Pill } from "@/admin/ui";
import { ProductDialog, CategoryDialog } from "@/admin/ProductDialogs";
import { PriceReplyPanel } from "@/admin/PriceReplyPanel";

const tone = { in_stock: "green", low_stock: "orange", unavailable: "grey", coming_soon: "blue" };

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [edits, setEdits] = useState({});
  const [filter, setFilter] = useState("");
  const [dlg, setDlg] = useState(null);
  const [catDlg, setCatDlg] = useState(null);

  const load = () => {
    adminApi.get("/products").then((r) => setProducts(r.data));
    adminApi.get("/categories").then((r) => setCats(r.data));
  };
  useEffect(load, []);

  const catName = useMemo(() => Object.fromEntries(cats.map((c) => [c.id, c.name])), [cats]);
  const shown = products.filter((p) => !filter || p.name.toLowerCase().includes(filter.toLowerCase()));
  const setEdit = (id, k, v) => setEdits((e) => ({ ...e, [id]: { ...e[id], [k]: v } }));
  const dirty = Object.keys(edits).length;

  const saveBulk = async () => {
    const updates = Object.entries(edits).map(([id, e]) => ({
      id, ...(e.price !== undefined && { price: Number(e.price) }), ...(e.stock_qty !== undefined && { stock_qty: parseInt(e.stock_qty, 10) }), ...(e.active !== undefined && { active: e.active }),
    }));
    if (updates.some((u) => (u.price !== undefined && !(u.price > 0)) || (u.stock_qty !== undefined && !(u.stock_qty >= 0)))) return toast.error("Prices must be > 0 and stock ≥ 0");
    try { setProducts((await adminApi.post("/products/bulk", { updates })).data); setEdits({}); toast.success(`Saved ${updates.length} products`); } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    await adminApi.delete(`/products/${p.id}`); load();
  };
  const delCat = async (c) => {
    if (!window.confirm(`Delete category ${c.name}?`)) return;
    try { await adminApi.delete(`/categories/${c.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageTitle sub="Edit prices and stock inline, then save all at once." action={<Btn onClick={() => setDlg({})} data-testid="add-product-btn"><Plus className="w-4 h-4 inline mr-1" />Add product</Btn>}>Products & prices</PageTitle>
      <Tabs defaultValue="products">
        <TabsList className="mb-3"><TabsTrigger value="products" data-testid="tab-products">Products ({products.length})</TabsTrigger><TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger></TabsList>
        <TabsContent value="products">
          <div className="flex flex-wrap gap-2 items-center mb-3">
            <Input placeholder="Search product" value={filter} onChange={(e) => setFilter(e.target.value)} className="h-9 w-60 bg-white" data-testid="product-search-input" />
            <div className="ml-auto flex gap-2 items-center">
              {dirty > 0 && <span className="text-sm text-accent font-semibold" data-testid="unsaved-count">{dirty} unsaved</span>}
              <Btn variant="outline" disabled={!dirty} onClick={() => setEdits({})} data-testid="discard-bulk-btn">Discard</Btn>
              <Btn disabled={!dirty} onClick={saveBulk} data-testid="save-bulk-btn">Save changes</Btn>
            </div>
          </div>
          <div className="bg-white border border-stone-200 rounded-md overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Product</TableHead><TableHead>Category</TableHead><TableHead className="w-32">Price ₹</TableHead><TableHead className="w-28">Stock</TableHead><TableHead>Status</TableHead><TableHead>Price date</TableHead><TableHead>Live</TableHead><TableHead /></TableRow></TableHeader>
              <TableBody>
                {shown.map((p) => {
                  const e = edits[p.id] || {};
                  return (
                    <TableRow key={p.id} className={edits[p.id] ? "bg-amber-50/60" : ""} data-testid={`admin-product-row-${p.id}`}>
                      <TableCell><div className="flex items-center gap-2"><img src={imgUrl(p.image)} alt="" className="w-9 h-9 rounded object-cover" /><div><p className="font-semibold">{p.name}</p><p className="text-xs text-stone-500">{p.unit}</p></div></div></TableCell>
                      <TableCell className="text-xs">{catName[p.category_id]}</TableCell>
                      <TableCell>{p.price_link ? <span className="text-sm font-semibold" title="Auto-priced from vegetable" data-testid={`price-auto-${p.id}`}>₹{p.price} <span className="text-[10px] text-secondary font-bold">AUTO</span></span> : <Input type="number" step="0.5" value={e.price ?? p.price} onChange={(ev) => setEdit(p.id, "price", ev.target.value)} className="h-8 w-24" data-testid={`price-input-${p.id}`} />}</TableCell>
                      <TableCell><Input type="number" value={e.stock_qty ?? p.stock_qty} onChange={(ev) => setEdit(p.id, "stock_qty", ev.target.value)} className="h-8 w-20" data-testid={`stock-input-${p.id}`} /></TableCell>
                      <TableCell><Pill tone={tone[p.stock_status]} testId={`admin-stock-status-${p.id}`}>{p.stock_status.replace("_", " ")}</Pill></TableCell>
                      <TableCell className="text-xs">{p.price_updated_date}</TableCell>
                      <TableCell><Switch checked={e.active ?? p.active} onCheckedChange={(v) => setEdit(p.id, "active", v)} data-testid={`active-switch-${p.id}`} /></TableCell>
                      <TableCell className="whitespace-nowrap">
                        <button onClick={() => setDlg(p)} className="p-1.5 text-stone-500 hover:text-primary" data-testid={`edit-product-${p.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => del(p)} className="p-1.5 text-stone-500 hover:text-accent" data-testid={`delete-product-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <PriceReplyPanel />
        </TabsContent>
        <TabsContent value="categories">
          <div className="mb-3"><Btn onClick={() => setCatDlg({})} data-testid="add-category-btn"><Plus className="w-4 h-4 inline mr-1" />Add category</Btn></div>
          <div className="bg-white border border-stone-200 rounded-md divide-y divide-stone-100">
            {cats.map((c) => (
              <div key={c.id} className="flex items-center gap-3 p-3" data-testid={`admin-category-${c.id}`}>
                <img src={imgUrl(c.image)} alt="" className="w-9 h-9 rounded object-cover" />
                <p className="font-semibold flex-1">{c.name} <span className="text-xs text-stone-400">· sort {c.sort}</span></p>
                <button onClick={() => setCatDlg(c)} className="p-1.5 text-stone-500 hover:text-primary" data-testid={`edit-category-${c.id}`}><Pencil className="w-4 h-4" /></button>
                <button onClick={() => delCat(c)} className="p-1.5 text-stone-500 hover:text-accent" data-testid={`delete-category-${c.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
      {dlg && <ProductDialog product={dlg} cats={cats} products={products} onClose={() => setDlg(null)} onSaved={load} />}
      {catDlg && <CategoryDialog category={catDlg} onClose={() => setCatDlg(null)} onSaved={load} />}
    </div>
  );
}
